import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { CookieConsentCategory, CookieConsentState } from './types';

interface CookiePreferencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: CookieConsentCategory[];
  initialConsent: CookieConsentState;
  primaryColor?: string;
  onSave: (consent: CookieConsentState) => void;
}

const DEFAULT_CATEGORIES: CookieConsentCategory[] = [
  { id: 'necessary', label: 'Necessary', description: 'Required for the site to work', required: true },
  { id: 'analytics', label: 'Analytics', description: 'Help us understand how you use the site', required: false },
  { id: 'marketing', label: 'Marketing', description: 'Used for advertising', required: false },
];

export function CookiePreferencesModal({
  open,
  onOpenChange,
  categories = DEFAULT_CATEGORIES,
  initialConsent,
  primaryColor = '#2563eb',
  onSave,
}: CookiePreferencesModalProps) {
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (open) {
      setPrefs({
        necessary: initialConsent.necessary,
        analytics: initialConsent.analytics,
        marketing: initialConsent.marketing,
      });
    }
  }, [open, initialConsent.necessary, initialConsent.analytics, initialConsent.marketing]);

  const handleSave = () => {
    onSave({
      necessary: true,
      analytics: prefs.analytics ?? false,
      marketing: prefs.marketing ?? false,
      timestamp: new Date().toISOString(),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cookie preferences</DialogTitle>
          <DialogDescription>
            Choose which categories of cookies you allow.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {categories.map((cat) => (
            <div key={cat.id} className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-1 flex-1">
                <Label className="text-sm font-medium">{cat.label}</Label>
                {cat.description && (
                  <p className="text-xs text-muted-foreground">{cat.description}</p>
                )}
              </div>
              <Switch
                checked={cat.required ? true : (prefs[cat.id] ?? false)}
                onCheckedChange={(checked) =>
                  !cat.required && setPrefs((p) => ({ ...p, [cat.id]: !!checked }))
                }
                disabled={!!cat.required}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            style={primaryColor ? { backgroundColor: primaryColor } : undefined}
          >
            Save preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
